import PropTypes from 'prop-types';
import {
  getINRLocaleStringWithCurrency,
  getDateModifiedFormat,
} from '../../utils/commonUtils';
import {
  getLabourPartPaymentsByID,
  getPartPaymentsByID,
  getPoPaymentsByID,
} from '../../utils/apiUtils/common';
import { useQuery } from '@tanstack/react-query';
import { DISCOUNT_TYPES } from '../../utils/constants';
import { useMemo } from 'react';

const TransactionDetailsModal = ({ open, onClose, selectedItem }) => {
  const paymentApiMap = {
    po: getPoPaymentsByID,
    partpayment: getPartPaymentsByID,
    labour: getLabourPartPaymentsByID,
  };

  const id = selectedItem?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['payments', selectedItem?.type, id],
    queryFn: async () => {
      if (!selectedItem?.type || !id) return null;
      return await paymentApiMap[selectedItem?.type](selectedItem?.id);
    },
    enabled: !!selectedItem?.type && !!id,
  });

  if (!open || !data) return null;

  const getPricingCalculation = (
    materialsRequested,
    discountType,
    discount,
    adjustment
  ) => {
    const totalSum = materialsRequested?.reduce(
      (partialSum, it) =>
        partialSum +
        (it.price || 0) * (it.actualVendorQuantity || it.quantity || 0),
      0
    );

    const cgstGroup = {};
    const sgstGroup = {};

    materialsRequested?.forEach((it) => {
      const cgst = it?.cgst || 0;
      const sgst = it?.sgst || 0;
      const amount =
        (it.price || 0) * (it.actualVendorQuantity || it.quantity || 0);

      if (!cgstGroup[cgst]) cgstGroup[cgst] = 0;
      if (!sgstGroup[sgst]) sgstGroup[sgst] = 0;

      cgstGroup[cgst] += (cgst * amount) / 100;
      sgstGroup[sgst] += (sgst * amount) / 100;
    });

    const cgstCost = Object.values(cgstGroup).reduce(
      (sum, value) => sum + value,
      0
    );
    const sgstCost = Object.values(sgstGroup).reduce(
      (sum, value) => sum + value,
      0
    );

    const sumWithGst = totalSum + cgstCost + sgstCost;
    const discountVal =
      discountType === DISCOUNT_TYPES.PERCENTAGE
        ? sumWithGst * ((discount || 0) / 100)
        : discount || 0;
    const sumWithDiscount = sumWithGst - discountVal;
    const finalSum = sumWithDiscount + (adjustment || 0);

    return { finalSum, totalSum, cgstGroup, sgstGroup, discountVal };
  };

  const { finalSum, totalSum, cgstGroup, sgstGroup, discountVal } =
    getPricingCalculation(
      data?.poQuantityPriceList,
      data?.discountType,
      data?.discount,
      data?.adjustment
    );

  console.log(finalSum);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="bg-[#232C3D] text-white p-5 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Transaction Details</h2>

          <button
            onClick={onClose}
            className="text-white rounded-full hover:bg-white/20 transition size-8 flex items-center justify-center text-xl"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-6 text-gray-800">
          {/* Transaction Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Project Name</p>
              <p className="text-lg font-semibold">
                {selectedItem?.projectName}
              </p>
            </div>

            {/* Badge */}
            {data.additionalAmount && (
              <div className="flex justify-start">
                <Tag text="Additional Amount" />
              </div>
            )}
          </div>

          {/* Amount Box */}
          <div className="w-full bg-gray-50 border rounded-xl p-6 text-center shadow-sm">
            <p className="text-base text-gray-500 mb-1">Amount</p>
            <p
              className={`text-2xl font-bold ${
                selectedItem.inflow ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {selectedItem.type === 'po'
                ? getINRLocaleStringWithCurrency(finalSum || 0, 0)
                : getINRLocaleStringWithCurrency(data.amount || 0, 0)}
            </p>
          </div>

          {/* Dynamic Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount Details */}
            {renderField(
              'Item Name',
              data.poQuantityPriceList?.[0]?.inventoryDetails?.inventoryName
            )}
            {renderField('Quantity', data.poQuantityPriceList?.[0]?.quantity)}
            {renderField(
              'Price per Unit',
              data.poQuantityPriceList?.[0]?.price
            )}
            {renderField('Date', data?.date)}
            {renderField('Category', data?.category)}
            {renderField('Description', data?.description)}
            {renderField('Reference Number', data?.reference)}
            {renderField('Payment Mode', data?.paymentMode)}
            {renderField('Payment Made By', data?.paymentMadeBy)}
            {renderField('Payment Received By', data?.paymentReceivedBy)}
            {renderField('GST No', data?.gstNo)}
            {/* Basic PO Info */}
            {renderField('Bill Number', data?.billNumber)}
            {renderField('Bill Date', data?.billDate)}
            {renderField('Receipt Number', data?.receiptNumber)}
            {renderField('Work Order ID', data?.workOrderId)}
            {renderField('Priority', data?.priorityEnum)}
            {/* Vendor Info */}
            {renderField('Vendor Name', data.vendor?.vendorFirstName)}
            {renderField('Vendor Company', data.vendor?.companyName)}
            {renderField('Vendor Phone', data.vendor?.phoneNumber)}
            {renderField('Vendor Email', data.vendor?.email)}
            {renderField(
              'Vendor Address',
              data.vendor?.billingAddress?.fullAddress
            )}
            {/* Site Info */}
            {renderField('Delivery Site', data.toSite?.roomName)}
          </div>

          {/* Attachments */}
          {Array.isArray(data.attachments) && data.attachments.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2 font-medium">
                Attachments
              </p>
              <div className="space-y-3">
                {data.attachments.map((file, i) => (
                  <a
                    key={i}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between bg-gray-50 border p-3 rounded-lg hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📎</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">{file.size}</p>
                      </div>
                    </div>
                    <span>➡️</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {data.notes && (
            <div>
              <p className="text-sm text-gray-500 mb-2 font-medium">Notes</p>
              <div className="bg-yellow-50 text-yellow-700 border border-yellow-200 p-4 rounded-lg text-sm">
                {data.notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TransactionDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedItem: PropTypes.object,
  type: PropTypes.string,
};

export default TransactionDetailsModal;

const renderField = (label, value) => {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-base font-medium text-gray-800">{value}</p>
    </div>
  );
};

const Tag = ({ text }) => (
  <span
    className="inline-block px-2 py-1 rounded-full text-xs font-semibold"
    style={{
      backgroundColor: '#FFF4CC',
      color: '#b68900',
      border: '1px solid #e1c75f',
    }}
  >
    {text}
  </span>
);

Tag.propTypes = {
  text: PropTypes.string.isRequired,
  color: PropTypes.string,
};
